import { IUser } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Col, Divider, Flex, Row } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  AccessCountWrapper,
  AccessDateWrapper,
  AccessTag,
  DateTextWrapper,
  DateWrapper,
  ItemWrapper,
  TitleWrapper,
  Wrapper
} from './StudentHistory.styles';

import { StudentConnectionStatusTag } from '@/components/StudentConnectionStatusTag';
import { StudentPaymentStatusTag } from '@/components/StudentPaymentStatusTag';
import { useAccessHistoryByUser } from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info';

type Props = {
  student: IUser;
};

export const StudentHistory: React.FC<Props> = ({ student }) => {
  const { t } = useTranslation();
  const { userInfo } = useUserInfoByUser(student._id);
  const { accessHistory, groupedAccessHistory, calcSessionDuration } = useAccessHistoryByUser(
    student._id
  );

  return (
    <Row gutter={16}>
      <Col span={8}>
        <Wrapper>
          <TitleWrapper>{t('STATUS_HISTORY')}</TitleWrapper>
          <Divider style={{ margin: 0 }} />
          <ItemWrapper>
            {userInfo?.connectionStatusInfo?.logs?.map(({ status, date }) => (
              <Flex key={`${status}-${date}`} align="center" justify="space-between">
                <StudentConnectionStatusTag userConnectionStatus={status} />
                <DateWrapper>{dateProvider.format(date, 'dd/MM/yyyy hh:mm:ss a')}</DateWrapper>
              </Flex>
            ))}
          </ItemWrapper>
        </Wrapper>
      </Col>
      <Col span={8}>
        <Wrapper>
          <TitleWrapper>{t('PAYMENT_HISTORY')}</TitleWrapper>
          <Divider style={{ margin: 0 }} />
          <ItemWrapper>
            {userInfo?.paymentStatusInfo &&
              userInfo.paymentStatusInfo.logs.map(({ status, date }, index) => (
                <Flex key={`${status}-${index}`} align="center" justify="space-between">
                  <StudentPaymentStatusTag paymentStatus={status} />
                  <DateWrapper>{dateProvider.format(date, 'dd/MM/yyyy hh:mm:ss a')}</DateWrapper>
                </Flex>
              ))}
          </ItemWrapper>
        </Wrapper>
      </Col>
      <Col span={8}>
        <Wrapper>
          <Flex justify="space-between" align="center">
            <TitleWrapper>{t('ACCESS_HISTORY')}</TitleWrapper>
            <AccessCountWrapper>
              {t('ACCESS_HISTORY_COUNT', { count: accessHistory?.length ?? 0 })}
            </AccessCountWrapper>
          </Flex>
          <Divider style={{ margin: 0 }} />
          <ItemWrapper>
            {groupedAccessHistory &&
              Object.entries(groupedAccessHistory).map(([key, logs]) => (
                <Flex key={key} vertical gap={16}>
                  <DateTextWrapper>{key}</DateTextWrapper>
                  {logs.map(log => {
                    const duration = calcSessionDuration(log);
                    return (
                      <Flex key={log._id} align="center" justify="space-between">
                        <AccessTag>{t('ACCESS_LABEL')}</AccessTag>
                        <AccessDateWrapper>
                          {dateProvider.format(log.createdAt, 'dd/MM/yyyy hh:mm:ss a')}
                          {duration && ` - (${duration})`}
                        </AccessDateWrapper>
                      </Flex>
                    );
                  })}
                </Flex>
              ))}
          </ItemWrapper>
        </Wrapper>
      </Col>
    </Row>
  );
};
